import React, { useState, useMemo, useEffect, useRef } from "react";
import { toast } from "react-toastify";



import { CreditCard, Banknote } from "lucide-react";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { useSettings } from "../context/SettingsContext";
import SquarePaymentVoucher from "./SquarePaymentVoucher";

const StripeForm = ({ clientSecret, amount, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
      });

      setLoading(false);

      if (error) {
        toast.error(error.message);
      } else if (
        paymentIntent?.status === "succeeded" ||
        paymentIntent?.status === "requires_capture"
      ) {
        toast.success("✅ Stripe Payment Successful!");
        onSuccess(paymentIntent);
      }
    } catch (err) {
      console.error("Stripe payment error:", err);
      toast.error("❌ Payment failed");
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* 🔥 Apple Pay / Google Pay / Cards */}
      <PaymentElement />

      <button
        type="submit"
        disabled={!stripe || loading}
        className="w-full px-4 py-2 rounded-lg bg-blue-600 text-white"
      >
        {loading ? "Processing..." : `Pay $${amount.toFixed(2)}`}
      </button>
    </form>
  );
};


const GiftVoucherPaymentModal = ({ isOpen, onClose, amount, onSuccess, tempOrderId }) => {
  const [selectedMethod, setSelectedMethod] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [stripePromise, setStripePromise] = useState(null);
  const [showSquare, setShowSquare] = useState(false);
  const [windcaveLink, setWindcaveLink] = useState("");
  const [windcaveSession, setWindcaveSession] = useState(null);
  const [polling, setPolling] = useState(false);
  const [loadingIntent, setLoadingIntent] = useState(false);
  const [iframeError, setIframeError] = useState(false);
useEffect(() => {
  if (!isOpen) {
    setIframeError(false);
  }
}, [isOpen]);

  const { settings } = useSettings();
  const pollRef = useRef(null);

  const filteredMethods = useMemo(() => {
    const methods = [];
    if (settings?.is_stripe == 1)
      methods.push({ id: "stripe", label: "Stripe", icon: CreditCard });
    if (settings?.is_windcave == 1)
      methods.push({ id: "windcave", label: "Windcave", icon: Banknote });
    if (settings?.is_squareup == 1)
      methods.push({ id: "squareup", label: "Squareup", icon: Banknote });

        if (settings?.is_xendit == 1)
        methods.push({
            id: "xendit",
            label: "Xendit",
            icon: CreditCard
        });


    return methods;
  }, [settings]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedMethod("");
      setClientSecret("");
      setWindcaveLink("");
      setWindcaveSession(null);
      setShowSquare(false);
      clearWindcavePolling();
      setLoadingIntent(false);
    }
  }, [isOpen]);

  const clearWindcavePolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
    setPolling(false);
  };

  const handlePayment = async () => {
    if (!selectedMethod) return toast.error("❌ Please select a payment method");

    // ✅ STRIPE
    if (selectedMethod === "stripe") {
      try {
        setLoadingIntent(true);
        const { data } = await axios.post(`${window.BASE_API_URL}stripeintents`, {
          orderId: `GIFT-${Date.now()}`,
          amount,
          currency: localStorage.getItem("currency_name") || "usd",
          mode: 'gift'
        });
        if (data?.client_secret) {
          setClientSecret(data.client_secret);
          if (!stripePromise && settings?.stripe_api_key) {
            const stripe = await loadStripe(settings.stripe_api_key);
            setStripePromise(stripe);
          }
        } else toast.error("❌ Failed to create Stripe payment intent");
      } catch (err) {
        console.error("Stripe init error:", err);
        toast.error("❌ Stripe Init Failed");
      } finally {
        setLoadingIntent(false);
      }
    }


    // ✅ XENDIT
if (selectedMethod === "xendit") {

    try {

        setLoadingIntent(true);

        const { data } = await axios.post(
            `${window.BASE_API_URL}xendit/create-payment`,
            {
                orderId: tempOrderId,
                amount,
                currency:
                    localStorage.getItem("currency_name") || "IDR",
                type: "voucher",
                temp_order_id: tempOrderId
            }
        );

        if (
            data.success &&
            data.checkout_url
        ) {

            window.location.href =
                data.checkout_url;

            return;
        }

        toast.error(
            data.message ||
            "Xendit init failed"
        );

    }
    catch (error) {

        toast.error(
            error?.response?.data?.message ||
            "Xendit payment failed"
        );

    }
    finally {

        setLoadingIntent(false);

    }
}

    // ✅ WINDCAVE
  // ✅ WINDCAVE
if (selectedMethod === "windcave") {
  try {
    setLoadingIntent(true);

    const orderId = `GIFT-${Date.now()}`;

    const { data } = await axios.post(`${window.BASE_API_URL}windcave_request`, {
      orderno: orderId,
       temp_order_id: tempOrderId, 
      amount: amount.toFixed(2),
      currency: localStorage.getItem("currency_name") || "USD",

      // 🔥 redirect urls  
      success_url: `${window.location.origin}/windcave-success?orderno=${orderId}`,
      fail_url: `${window.location.origin}/windcave-fail`,
    });

    if (data?.success && data?.link) {
      // 🔥 DIRECT REDIRECT  
      window.location.href = data.link;
    } else {
      toast.error("❌ Windcave initialization failed");
    }
  } catch (err) {
    console.error("Windcave init error:", err);
    toast.error("❌ Windcave init failed");
  } finally {
    setLoadingIntent(false);
  }
}



    // ✅ SQUARE
    if (selectedMethod === "squareup") setShowSquare(true);
  };

  const startWindcavePolling = (sessionId) => {
    if (!sessionId) {
      console.warn("No sessionId provided for Windcave polling");
      return;
    }
    setPolling(true);
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts += 1;
      try {
        const { data } = await axios.post(`${window.BASE_API_URL}windcave_status`, {
          session_id: sessionId,
          orderno: sessionId,
        });

        // ✅ SUCCESS
       if (data?.success && ["COMPLETED", "SUCCESS"].includes(data.status)) {
  clearWindcavePolling();
  toast.success("✅ Windcave Payment Successful!");

  const wc = data.data || {};

  const paymentIntent = {
    id: wc.transaction_id || wc.txnRef || `GIFT-WC-${Date.now()}`,
    object: "payment_intent",
    amount: Math.round(amount * 100),
    currency: wc.currency || localStorage.getItem("currency_name") || "USD",
    status: "succeeded",
    provider: "windcave",
    payment_method_types: ["card"],
    payment_method: {
      type: "windcave_card",
      card: {
        brand: wc.cardScheme || "card",
        last4: wc.cardNumber?.slice(-4) || "",
        holder: wc.cardHolderName || "",
        authCode: wc.authCode || "",
      },
    },
    created: Date.now(),
    raw: data,
  };

  onSuccess(paymentIntent);
  onClose();
}


        // ❌ FAILED
        else if (data?.success && data.status === "FAILED") {
          clearWindcavePolling();
          toast.error("❌ Windcave payment failed");
        }
      } catch (err) {
        console.debug("Windcave poll error", err);
      }

      if (attempts >= 60) {
        clearWindcavePolling();
        toast.error("❌ Windcave payment timed out");
      }
    }, 2000);
  };

  useEffect(() => {
    return () => clearWindcavePolling();
  }, []);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9998]">
        <div className="bg-white rounded-2xl shadow-2xl w-[420px] p-6 relative">
          <h2 className="text-2xl font-semibold mb-3 text-gray-800"  style={{ color: settings?.cart_btn_color || "#111827" }}>
            💳 Pay for Gift Vouchers
          </h2>
          <p className="mb-2 text-gray-500">Amount</p>
          <p className="mb-6 text-xl font-bold text-gray-800">
            ${amount.toFixed(2)}
          </p>

          {/* --- Payment Method Selection --- */}
          {!clientSecret && !windcaveLink && !showSquare && (
            <>
              <div className="space-y-3">
                {filteredMethods.map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => setSelectedMethod(id)}
                    className={`flex items-center gap-3 w-full p-4 rounded-xl border transition-all ${
                      selectedMethod === id
                        ? "border-blue-500 bg-blue-50 shadow-sm"
                        : "border-gray-300 hover:border-blue-400 hover:bg-gray-50"
                    }`}
                  >
                    <Icon
                      size={22}
                      className={
                        selectedMethod === id ? "text-blue-600" : "text-gray-500"
                      }
                    />
                    <span
                      className={`text-base font-medium ${
                        selectedMethod === id
                          ? "text-blue-700"
                          : "text-gray-700"
                      }`}
                    >
                      {label}
                    </span>
                  </button>
                ))}
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 transition"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                 style={{
                  background: settings?.cart_btn_color || "#000000ff",
                  color: settings?.cart_btn_text_color || "#ffffff",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background =
                    settings?.cart_btn_hover || "#f59e0b";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background =
                    settings?.cart_btn_color || "#ffffff";
                }}
                  className="px-5 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white shadow-md transition"
                  onClick={handlePayment}
                >
                  {loadingIntent ? "Initializing..." : "Continue"}
                </button>
              </div>
            </>
          )}

          {/* --- STRIPE --- */}
          {clientSecret && stripePromise && (
            <div>
              <Elements
  stripe={stripePromise}
  options={{
    clientSecret,
    appearance: {
      theme: "stripe",
    },
  }}
>

                <StripeForm
                  clientSecret={clientSecret}
                  amount={amount}
                  onSuccess={onSuccess}
                />
              </Elements>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 transition"
                  onClick={() => setClientSecret("")}
                >
                  ⬅ Back
                </button>
              </div>
            </div>
          )}

          {/* --- WINDCAVE --- */}
         {windcaveLink && (
  <div>
    <div className="mb-3 text-sm text-gray-600">
      Complete payment below. If blocked, you will be redirected.
    </div>

    <div className="w-full h-[520px] border rounded-lg overflow-hidden relative">
      {!iframeError ? (
        <iframe
          title="windcave-inline"
          src={windcaveLink}
          className="w-full h-full"
          frameBorder="0"
          allowFullScreen
          onError={() => {
            setIframeError(true);
            window.location.href = windcaveLink; // fallback
          }}
          onLoad={(e) => {
            try {
              if (!e.target.contentWindow) {
                setIframeError(true);
                window.location.href = windcaveLink;
              }
            } catch (err) {
              setIframeError(true);
              window.location.href = windcaveLink;
            }
          }}
        />
      ) : (
        <div className="p-4 text-center">
          <p className="text-red-500 mb-3">
            Inline payment blocked. Redirecting…
          </p>
          {window.location.href = windcaveLink}
        </div>
      )}
    </div>

    <div className="flex justify-end gap-3 mt-4">
      <button
        className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 transition"
        onClick={() => {
          setWindcaveLink("");
          setWindcaveSession(null);
          setIframeError(false);
          clearWindcavePolling();
        }}
      >
        ⬅ Back
      </button>
    </div>
  </div>
)}


          {/* --- SQUARE --- */}
          {showSquare && (
            <div>
              <div className="mb-3 text-sm text-gray-600">Square Payment</div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 transition"
                  onClick={() => setShowSquare(false)}
                >
                  ⬅ Back
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Square Payment Portal */}
      <SquarePaymentVoucher
        open={showSquare}
        onClose={() => setShowSquare(false)}
        amount={amount}
        settings={settings}
        onSuccess={(paymentIntent) => {
          onSuccess(paymentIntent);
          setShowSquare(false);
          onClose();
        }}
      />
    </>
  );
};

export default GiftVoucherPaymentModal;
